<template>
    <HeaderComponent />

    <ScrollableView class="view-container has-footer" :contentClass="'production-view-new'">
        <div class="grid">
            <template v-if="showNewestFirst">
                <ProductionViewSection
                    v-if="showNotReadySections"
                    :fulfillments="inPreparationFulfillments"
                    :sectionName="trans.production.inPreparation"
                    class="preparation"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    v-if="showNotReadySections"
                    :fulfillments="readyShortlyFulfillments"
                    :sectionName="trans.production.almostReady"
                    class="almostready"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    :fulfillments="readyFulfillments"
                    :sectionName="trans.production.ready"
                    class="ready"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    v-if="showFulfilledNotPaidSection"
                    :fulfillments="fulfilledNotPaidFulfillments"
                    :sectionName="trans.production.fulfilledNotPaid"
                    class="fulfillednotpaid"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />
            </template>
            <template v-else>
                <ProductionViewSection
                    v-if="showFulfilledNotPaidSection"
                    :fulfillments="fulfilledNotPaidFulfillments"
                    :sectionName="trans.production.fulfilledNotPaid"
                    class="fulfillednotpaid"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    :fulfillments="readyFulfillments"
                    :sectionName="trans.production.ready"
                    class="ready"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    v-if="showNotReadySections"
                    :fulfillments="readyShortlyFulfillments"
                    :sectionName="trans.production.almostReady"
                    class="almostready"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />

                <ProductionViewSection
                    v-if="showNotReadySections"
                    :fulfillments="inPreparationFulfillments"
                    :sectionName="trans.production.inPreparation"
                    class="preparation"
                    :tileSize="fulfillmentTileSize"
                    :currentTime="currentTime"
                />
            </template>
        </div>
    </ScrollableView>

    <ProductionViewFooter :currentSettings="currentSettings" />
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import {
        FulfillmentProductionState,
        FulfilmentTileSize,
        NewOrdersViewMode,
        OrderStateMode,
    } from "@/data/model/Enums";
    import type { IExtendedOperatorFulfillment, IOperatorFulfillment } from "@/data/model/Operator";
    import type { IOperatorSettings } from "@/data/model/User";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { ProductionContext } from "@/operator/context/ProductionContext";
    import { FulfillmentHelper } from "@/operator/data/FulfillmentHelper";
    import { ProductionViewFocusMonitor } from "@/operator/elements/ProductionViewFocusMonitor";
    import { type FulfillmentClickedEventData, operatorEvents } from "@/operator/events/operatorEvents";
    import { trans } from "@/operator/localization/Trans";
    import { FulfillmentModal } from "@/operator/modal/FulfillmentModal";
    import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
    import { FulfillmentSoundNotifications } from "@/operator/ux/SoundNotifications";
    import { translations } from "@/shared/localization/translations";
    import { computed, onMounted, ref } from "vue";

    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import DeliveryAddressForm from "@/components/pay/DeliveryAddressForm.vue";
    import HeaderComponent from "@/operator/components/layout/HeaderComponent.vue";
    import ProductionViewFooter from "@/operator/components/production/ProductionViewFooter.vue";
    import ProductionViewSection from "@/operator/components/production/ProductionViewSection.vue";

    const context = ProductionContext.getInstance();
    let timer: number | undefined;
    let fulfillmentModal: FulfillmentModal;
    let focusMonitor: ProductionViewFocusMonitor;

    const currentSettings = ref<IOperatorSettings | undefined>(undefined);

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);

    // sections display configuration
    const newOrdersViewMode = computed(() => currentSettings.value?.newOrdersViewMode ?? NewOrdersViewMode.AtTheTop);
    const orderStateMode = computed(() => currentSettings.value?.orderStateMode ?? OrderStateMode.All);
    const fulfillmentTileSize = computed(() => currentSettings.value?.fulfilmentTileSize ?? FulfilmentTileSize.Normal);

    const showNewestFirst = computed(() => newOrdersViewMode.value == NewOrdersViewMode.AtTheTop);
    const showNotReadySections = computed(() => orderStateMode.value !== OrderStateMode.Ready);
    const showFulfilledNotPaidSection = computed(
        () => orderStateMode.value === OrderStateMode.All || orderStateMode.value === OrderStateMode.NotPaid
    );

    // fulfillment sections logic
    const inPreparationFulfillments = computed(() => {
        return showNotReadySections.value
            ? getFulfillmentsForSection(context.extendedFulfillments.value, FulfillmentProductionState.InPreparation)
            : [];
    });

    const readyShortlyFulfillments = computed(() =>
        showNotReadySections.value
            ? getFulfillmentsForSection(context.extendedFulfillments.value, FulfillmentProductionState.ReadyShortly)
            : []
    );
    const readyFulfillments = computed(() =>
        getFulfillmentsForSection(context.extendedFulfillments.value, FulfillmentProductionState.Ready)
    );
    const fulfilledNotPaidFulfillments = computed(() =>
        showFulfilledNotPaidSection.value
            ? getFulfillmentsForSection(context.extendedFulfillments.value, FulfillmentProductionState.FulfilledNotPaid)
            : []
    );

    function getFulfillmentsForSection(
        fulfillments: IExtendedOperatorFulfillment[],
        state: FulfillmentProductionState
    ) {
        const filteredFulfillments = fulfillments.filter((f) => f.productionState == state);
        const orderMultiplier = showNewestFirst.value ? 1 : -1;

        filteredFulfillments.sort((a, b) => {
            return orderMultiplier * FulfillmentHelper.newestFirstComparator(a, b, context.groups.value);
        });

        return filteredFulfillments;
    }
    // end new

    function init() {
        fulfillmentModal = new FulfillmentModal();
        focusMonitor = new ProductionViewFocusMonitor(context);

        operatorEvents.context.authenticationChanged.add((isAuthenticated) => {
            //reset on logout
            if (!isAuthenticated) {
                context.reset();
            }
        });

        onBecomeVisible();
    }

    async function onBecomeVisible() {
        operatorEvents.context.settingsChanged.add(onSettingsChanged);
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded);
        operatorEvents.productionContext.FulfillmentAdded.add(updateFulfillments);
        operatorEvents.productionContext.FulfillmentChanged.add(updateFulfillments);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.add(updateFulfillments);
        operatorEvents.productionContext.FulfillmentRemoved.add(updateFulfillments);
        operatorEvents.fulfillmentDetailsPopup.orderNumberSelected.add(fulfillmentPopupOrderNumberSelected);
        operatorEvents.fulfillmentDetailsPopup.showDeliveryAddressClicked.add(openDeliveryAddressPopup);
        operatorEvents.productionController.onFulfillmentClick.add(onFulfillmentClick);

        refreshSettings();
        await context.reloadAll();

        focusMonitor.enable();

        startTimer();
    }

    function onBecomeInvisible() {
        operatorEvents.context.settingsChanged.remove(onSettingsChanged);
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded);
        operatorEvents.productionContext.FulfillmentChanged.remove(FulfillmentSoundNotifications.playSoundIfNeeded);
        operatorEvents.productionContext.FulfillmentAdded.remove(updateFulfillments);
        operatorEvents.productionContext.FulfillmentChanged.remove(updateFulfillments);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.remove(updateFulfillments);
        operatorEvents.productionContext.FulfillmentRemoved.remove(updateFulfillments);
        operatorEvents.fulfillmentDetailsPopup.orderNumberSelected.remove(fulfillmentPopupOrderNumberSelected);
        operatorEvents.fulfillmentDetailsPopup.showDeliveryAddressClicked.remove(openDeliveryAddressPopup);
        operatorEvents.productionController.onFulfillmentClick.remove(onFulfillmentClick);

        focusMonitor.disable();

        stopTimer();
    }

    function onFulfillmentClick(data: FulfillmentClickedEventData) {
        fulfillmentModal.setFulfillment(data.fulfillment, data.fulfillmentGroup).open();
    }

    async function onSettingsChanged() {
        refreshSettings();
        await context.reloadAll();
    }

    function updateFulfillments() {
        // verify if fulfillment is still available in the context
        const popupFulfillmentId = fulfillmentModal?.fulfillment?.id;
        if (popupFulfillmentId === undefined) {
            return;
        }

        // if fulfillment is available in context - update fulfillment state
        // if not - close modal
        const fulfillmentDisplayedInModal = context.fulfillments.value.find((c) => c.id === popupFulfillmentId);
        if (fulfillmentDisplayedInModal != null) {
            fulfillmentModal.setFulfillment(
                fulfillmentDisplayedInModal,
                getFulfillmentGroup(fulfillmentDisplayedInModal)
            );
        } else {
            fulfillmentModal.close();
        }
    }

    function onTimeUpdated(date: Date) {
        if (fulfillmentModal !== undefined && fulfillmentModal.isOpen) {
            fulfillmentModal.updateTime(date);
        }
    }

    const currentTime = ref(new Date());
    function startTimer() {
        stopTimer();
        timer = setInterval(() => {
            currentTime.value = new Date();
            onTimeUpdated(currentTime.value);

            // todo: this function updates ready state for fulfillments. it should be moved to backend
            context.updateProductionState();
        }, 1000) as any;

        currentTime.value = new Date();
        onTimeUpdated(currentTime.value);
    }

    function stopTimer() {
        if (timer === undefined) return;

        clearInterval(timer);
        timer = undefined;
    }

    function refreshSettings() {
        currentSettings.value = OperatorClient.context.settings;
    }

    function getFulfillmentGroup(fulfillment: IOperatorFulfillment) {
        const fulfillmentGroup =
            fulfillment.fulfillmentGroupId !== undefined
                ? context.groups.value.find((c) => c.id === fulfillment.fulfillmentGroupId)
                : undefined;
        return fulfillmentGroup;
    }

    // functions
    async function openDeliveryAddressPopup(orderId: string) {
        const deliveryAddress = await orderDao.getOrderDeliveryAddress(orderId, {});

        let popup = Popup.show({
            component: DeliveryAddressForm,
            componentProps: {
                deliveryAddress: deliveryAddress,
                hide: () => {
                    popup?.close();
                },
            },
            showConfirmButton: false,
            closePopupOnOverlayClick: true,
            showCloseButton: true,
            title: translations.deliveryAddress.deliveryAddress,
        });
    }

    // register events
    onMounted(async () => {
        init();
        operatorEvents.productionController.onShown.add(onBecomeVisible);
        operatorEvents.productionController.onHidden.add(onBecomeInvisible);
    });

    function fulfillmentPopupOrderNumberSelected(orderId: string) {
        new OrderDetailsModal(orderId, async () => {
            refreshSettings();
            await context.reloadAll();
        }).show();
    }

    // register events
    onMounted(async () => {
        init();
        operatorEvents.productionController.onShown.add(onBecomeVisible);
        operatorEvents.productionController.onHidden.add(onBecomeInvisible);
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";

    .production-view-new .grid {
        > .section {
            padding: 15px 8px 0 8px;

            > .name {
                font-size: 16px;
                font-weight: 600;
                color: colors.$fontColor;
                line-height: 20px;
                padding: 0 6px;
            }

            > .name-large {
                font-size: 32px;
                font-weight: 600;
                color: colors.$fontColor;
                line-height: 40px;
                padding: 0 12px;
            }

            > .fulfillments {
                margin: 0 -6px;
                padding: 8px 0;

                .fulfillment-box {
                    display: inline-block;
                    vertical-align: top;
                }

                .fulfillment {
                    margin: 5px 6px;
                    width: 210px;
                    display: inline-block;
                    vertical-align: top;
                    -webkit-border-top-left-radius: 4px;
                    -webkit-border-top-right-radius: 4px;
                    -moz-border-radius-topleft: 4px;
                    -moz-border-radius-topright: 4px;
                    border-top-left-radius: 4px;
                    border-top-right-radius: 4px;
                    overflow: hidden;
                    cursor: pointer;

                    @include mixins.hover-and-active() {
                        > .head {
                            background-color: #373737;
                        }

                        > .body {
                            border-color: #dfdfdf;
                        }
                    }

                    &.is-unpaid {
                        > .head {
                            background-color: colors.$red;
                        }
                    }

                    > .head {
                        position: relative;
                        background-color: colors.$fontColor;
                        height: 64px;

                        > .info {
                            color: #fff;
                            padding: 5px 50px 0 11px;

                            > .order {
                                font-size: 18px;
                                line-height: 24px;
                                font-weight: 600;
                                text-transform: uppercase;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;

                                > .order-number-with-icon {
                                    display: flex;
                                    align-items: center;

                                    > .order-number {
                                        padding-right: 3px;
                                    }
                                }

                                > .groupName {
                                    white-space: nowrap;
                                    font-size: 12px;
                                    line-height: 15px;
                                    font-weight: normal;
                                    padding-right: 3px;
                                    overflow: hidden;
                                }
                            }

                            > .customerTable {
                                font-size: 12px;
                                line-height: 15px;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;

                                > .tableName {
                                    overflow: hidden;
                                    white-space: nowrap;
                                }

                                > .useCaseName {
                                    white-space: nowrap;
                                    display: inline-block;
                                    height: 22px;
                                    line-height: 22px;
                                    background-color: #fff;
                                    color: #484848;
                                    font-size: 12px;
                                    font-weight: 400;
                                    padding: 0 4px;
                                    margin-bottom: -10px;
                                    border-radius: 2px;
                                    overflow: hidden;
                                    max-width: 90px;
                                }
                            }

                            > .ordertime {
                                font-size: 12px;
                                line-height: 15px;
                            }
                        }

                        .timer {
                            position: absolute;
                            right: 5px;
                            top: 5px;
                            width: 42px;
                            background-color: #fff;
                            border-radius: 4px;
                            text-align: center;
                            padding: 4px 0 6px 0;

                            > .time {
                                font-size: 22px;
                                font-weight: 600;
                                line-height: 24px;
                                text-align: center;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;

                                > .unit {
                                    font-size: 8px;
                                    line-height: 14px;
                                    padding-left: 1px;
                                    display: inline-block;
                                    vertical-align: bottom;
                                }
                            }

                            > .lateicon {
                                height: 16px;
                                padding: 2px 0 2px 0;

                                > .fal {
                                    font-size: 14px;
                                    line-height: 16px;
                                }
                            }

                            &.green {
                                color: colors.$green;
                            }

                            &.orange {
                                color: colors.$orange;
                            }

                            &.red {
                                color: colors.$red;
                            }
                        }
                    }

                    > .body {
                        border: 1px solid #eaeaea;
                        border-top: none;
                        -webkit-border-bottom-right-radius: 4px;
                        -webkit-border-bottom-left-radius: 4px;
                        -moz-border-radius-bottomright: 4px;
                        -moz-border-radius-bottomleft: 4px;
                        border-bottom-right-radius: 4px;
                        border-bottom-left-radius: 4px;

                        &.has-no-header {
                            border-top: 1px solid #eaeaea !important;
                            -webkit-border-top-right-radius: 4px;
                            -webkit-border-top-left-radius: 4px;
                            -moz-border-radius-topright: 4px;
                            -moz-border-radius-topleft: 4px;
                            border-top-right-radius: 4px;
                            border-top-left-radius: 4px;
                        }

                        .order-number-container {
                            &.is-ready-shortly {
                                color: colors.$orange;
                            }

                            &.is-ready {
                                color: colors.$red;
                            }

                            .big-order-number {
                                line-height: 1.3;
                                text-align: center;
                                font-size: 3.3em;
                                font-weight: bold;
                            }

                            .big-order-daily-increment {
                                line-height: 1.3;
                                text-align: center;
                                font-size: 8em;
                                font-weight: bold;
                            }
                        }

                        .group-overview {
                            text-align: center;
                            text-overflow: ellipsis;
                            overflow: hidden;
                            white-space: nowrap;
                            font-size: 17px;
                            line-height: 19px;
                            padding: 3px 4px;

                            > .quantity {
                                display: inline-block;
                                vertical-align: bottom;
                                line-height: 22px;
                                font-size: 21px;
                                font-weight: 600;
                                color: colors.$green;
                            }

                            > .times {
                                display: inline-block;
                                vertical-align: bottom;
                                padding-right: 3px;
                                line-height: 17px;
                                font-size: 14px;
                                font-weight: 600;
                                color: colors.$green;
                            }
                        }

                        > .actions {
                            text-align: center;
                            padding: 3px;

                            > .button {
                                height: 34px;
                                display: inline-block;
                                vertical-align: top;
                                border-radius: 17px;
                                color: #fff;
                                font-size: 15px;
                                line-height: 34px;

                                &.increasetime {
                                    background-color: colors.$orange;
                                    width: 55px;
                                    margin-right: 2px;

                                    @include mixins.hover-and-active() {
                                        background-color: colors.$orangeHover;
                                    }
                                }

                                &.resolve {
                                    background-color: colors.$green;
                                    width: 110px;
                                    margin-left: 2px;

                                    @include mixins.hover-and-active() {
                                        background-color: colors.$greenHover;
                                    }

                                    > .fa-light.fa-person-carry-box {
                                        margin-right: 5px;
                                    }

                                    > .fa-cogs {
                                        margin-right: 0.2em;
                                    }
                                }

                                &.disabled,
                                &:disabled {
                                    cursor: default !important;

                                    &.increasetime {
                                        background-color: colors.$orangeDisabled !important;
                                    }

                                    &.resolve {
                                        background-color: colors.$greenDisabled !important;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            > .fulfillments-large {
                padding: 16px 0;

                .fulfillment-box {
                    display: inline-block;
                    vertical-align: top;
                }

                .fulfillment {
                    margin: 12px;
                    width: 420px;
                    display: inline-block;
                    vertical-align: top;
                    -webkit-border-top-left-radius: 8px;
                    -webkit-border-top-right-radius: 8px;
                    -moz-border-radius-topleft: 8px;
                    -moz-border-radius-topright: 8px;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    overflow: hidden;
                    cursor: pointer;

                    @include mixins.hover-and-active() {
                        > .head {
                            background-color: #373737;
                        }

                        > .body {
                            border-color: #dfdfdf;
                        }
                    }

                    &.is-unpaid {
                        > .head {
                            background-color: colors.$red;
                        }
                    }

                    > .head {
                        position: relative;
                        background-color: colors.$fontColor;
                        height: 128px;

                        > .info {
                            color: #fff;
                            padding: 5px 100px 0 5px;

                            > .order {
                                font-size: 36px;
                                line-height: 48px;
                                font-weight: 600;
                                text-transform: uppercase;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;

                                > .order-number-with-icon {
                                    display: flex;
                                    align-items: center;

                                    > .order-number {
                                        padding-right: 3px;
                                    }
                                }

                                > .groupName {
                                    white-space: nowrap;
                                    font-size: 24px;
                                    line-height: 30px;
                                    font-weight: normal;

                                    padding-right: 3px;
                                    overflow: hidden;
                                }
                            }

                            > .customerTable {
                                font-size: 24px;
                                line-height: 30px;
                                display: flex;
                                justify-content: space-between;
                                align-items: center;

                                > .tableName {
                                    overflow: hidden;
                                    white-space: nowrap;
                                }

                                > .useCaseName {
                                    white-space: nowrap;
                                    display: inline-block;
                                    height: 44px;
                                    line-height: 44px;
                                    background-color: #fff;
                                    color: #484848;
                                    font-size: 24px;
                                    font-weight: 400;
                                    padding: 0 8px;
                                    margin-bottom: -10px;
                                    border-radius: 2px;
                                    overflow: hidden;
                                    max-width: 180px;
                                }
                            }

                            > .ordertime {
                                font-size: 24px;
                                line-height: 30px;
                            }
                        }

                        .timer {
                            position: absolute;
                            right: 5px;
                            top: 5px;
                            width: 84px;
                            background-color: #fff;
                            border-radius: 8px;
                            text-align: center;
                            padding: 4px 0 6px 0;

                            > .time {
                                font-size: 44px;
                                font-weight: 600;
                                line-height: 48px;
                                text-align: center;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;

                                > .unit {
                                    font-size: 16px;
                                    line-height: 28px;
                                    padding-left: 1px;
                                    display: inline-block;
                                    vertical-align: bottom;
                                }
                            }

                            > .lateicon {
                                height: 32px;
                                padding: 2px 0 2px 0;

                                > .fal {
                                    font-size: 28px;
                                    line-height: 32px;
                                }
                            }

                            &.green {
                                color: colors.$green;
                            }

                            &.orange {
                                color: colors.$orange;
                            }

                            &.red {
                                color: colors.$red;
                            }
                        }
                    }

                    > .body {
                        border: 2px solid #eaeaea;
                        border-top: none;
                        -webkit-border-bottom-right-radius: 8px;
                        -webkit-border-bottom-left-radius: 8px;
                        -moz-border-radius-bottomright: 8px;
                        -moz-border-radius-bottomleft: 8px;
                        border-bottom-right-radius: 8px;
                        border-bottom-left-radius: 8px;

                        &.has-no-header {
                            border-top: 2px solid #eaeaea !important;
                            -webkit-border-top-right-radius: 8px;
                            -webkit-border-top-left-radius: 8px;
                            -moz-border-radius-topright: 8px;
                            -moz-border-radius-topleft: 8px;
                            border-top-right-radius: 8px;
                            border-top-left-radius: 8px;
                        }

                        .order-number-container {
                            &.is-ready-shortly {
                                color: colors.$orange;
                            }

                            &.is-ready {
                                color: colors.$red;
                            }
                            .big-order-number {
                                line-height: 1.3;
                                text-align: center;
                                font-size: 7em;
                                font-weight: bold;
                            }

                            .big-order-daily-increment {
                                line-height: 1.1;
                                text-align: center;
                                font-size: 17em;
                                font-weight: bold;
                            }
                        }

                        .group-overview {
                            text-align: center;
                            text-overflow: ellipsis;
                            overflow: hidden;
                            white-space: nowrap;
                            font-size: 34px;
                            line-height: 38px;
                            padding: 6px 8px;

                            > .quantity {
                                display: inline-block;
                                vertical-align: bottom;
                                line-height: 44px;
                                font-size: 42px;
                                font-weight: 600;
                                color: colors.$green;
                            }

                            > .times {
                                display: inline-block;
                                vertical-align: bottom;
                                padding-right: 3px;
                                line-height: 34px;
                                font-size: 28px;
                                font-weight: 600;
                                color: colors.$green;
                            }
                        }

                        > .actions {
                            text-align: center;
                            padding: 6px;

                            > .button {
                                height: 68px;
                                display: inline-block;
                                vertical-align: top;
                                border-radius: 34px;
                                color: #fff;
                                font-size: 30px;
                                line-height: 68px;

                                &.increasetime {
                                    background-color: colors.$orange;
                                    width: 110px;
                                    margin-right: 4px;

                                    @include mixins.hover-and-active() {
                                        background-color: colors.$orangeHover;
                                    }
                                }

                                &.resolve {
                                    background-color: colors.$green;
                                    width: 220px;
                                    margin-left: 4px;

                                    @include mixins.hover-and-active() {
                                        background-color: colors.$greenHover;
                                    }

                                    > .fa-light.fa-person-carry-box {
                                        margin-right: 5px;
                                    }

                                    > .fa-cogs {
                                        margin-right: 0.2em;
                                    }
                                }

                                &.disabled,
                                &:disabled {
                                    cursor: default !important;

                                    &.increasetime {
                                        background-color: colors.$orangeDisabled !important;
                                    }

                                    &.resolve {
                                        background-color: colors.$greenDisabled !important;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            .fulfillment {
                .cancelled {
                    color: colors.$pink !important;
                    text-decoration: line-through;
                    opacity: 0.7;

                    .quantity {
                        color: colors.$pink !important;
                    }
                }
            }
        }
    }
</style>
